
import React, { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, 
  CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import { Database } from '@/integrations/supabase/types';
import { CalendarRange, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface AnalyticsData {
  userSignups: any[];
  pointsActivity: any[];
  rewardRedemptions: any[];
  usersByTier: any[];
  activeUsers: any[];
  eventParticipation: any[];
  pointsDistribution: any[];
  tierConversionRate: any[];
}

const COLORS = ['#E4002B', '#8884d8', '#82ca9d', '#ffc658', '#8dd1e1', '#a4de6c', '#d0ed57', '#ffaa00'];

const AdminAnalytics = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData>({
    userSignups: [],
    pointsActivity: [],
    rewardRedemptions: [],
    usersByTier: [],
    activeUsers: [],
    eventParticipation: [],
    pointsDistribution: [],
    tierConversionRate: []
  });
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('week');
  const [activeTab, setActiveTab] = useState('users');
  const [metric, setMetric] = useState('signups');

  useEffect(() => {
    fetchAnalyticsData();
  }, [dateRange]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      
      // Get user signups by day
      const signupData = await fetchUserSignups();
      const pointsData = await fetchPointsActivity();
      const redemptionsData = await fetchRewardRedemptions();
      const tierData = await fetchUsersByTier();
      const activeUsersData = await fetchActiveUsers();
      const eventParticipationData = await fetchEventParticipation();
      const pointsDistributionData = await fetchPointsDistribution();
      const tierConversionData = await fetchTierConversionRates();
      
      setAnalyticsData({
        userSignups: signupData,
        pointsActivity: pointsData,
        rewardRedemptions: redemptionsData,
        usersByTier: tierData,
        activeUsers: activeUsersData,
        eventParticipation: eventParticipationData,
        pointsDistribution: pointsDistributionData,
        tierConversionRate: tierConversionData
      });
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      toast({
        title: t('admin.error'),
        description: t('admin.errorFetchingAnalytics'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUserSignups = async () => {
    // Get date range filter
    const { startDate } = getDateRange();
    
    const { data, error } = await supabase
      .from('customer_profiles')
      .select('created_at')
      .gte('created_at', startDate.toISOString());
      
    if (error) throw error;
    
    // Group by date
    const signupsByDate = data?.reduce((acc: any, profile) => {
      const date = new Date(profile.created_at).toISOString().split('T')[0];
      if (!acc[date]) {
        acc[date] = { date, count: 0 };
      }
      acc[date].count += 1;
      return acc;
    }, {});
    
    return Object.values(signupsByDate || {});
  };

  const fetchPointsActivity = async () => {
    // Get date range filter
    const { startDate } = getDateRange();
    
    const { data, error } = await supabase
      .from('point_transactions')
      .select('created_at, transaction_type, amount')
      .gte('created_at', startDate.toISOString());
      
    if (error) throw error;
    
    // Group by date and transaction type
    const pointsByDate = data?.reduce((acc: any, transaction) => {
      const date = new Date(transaction.created_at).toISOString().split('T')[0];
      
      if (!acc[date]) {
        acc[date] = { 
          date, 
          earned: 0, 
          redeemed: 0 
        };
      }
      
      if (transaction.transaction_type === 'earned') {
        acc[date].earned += transaction.amount;
      } else if (transaction.transaction_type === 'redeemed') {
        acc[date].redeemed += Math.abs(transaction.amount);
      }
      
      return acc;
    }, {});
    
    return Object.values(pointsByDate || {});
  };

  const fetchRewardRedemptions = async () => {
    // Get date range filter
    const { startDate } = getDateRange();
    
    const { data, error } = await supabase
      .from('customer_rewards')
      .select('redeemed_at, reward_id, rewards(name)')
      .gte('redeemed_at', startDate.toISOString());
      
    if (error) throw error;
    
    // Group by reward
    const redemptionsByReward = data?.reduce((acc: any, redemption) => {
      const rewardName = (redemption.rewards as any)?.name || 'Unknown';
      
      if (!acc[rewardName]) {
        acc[rewardName] = { name: rewardName, count: 0 };
      }
      
      acc[rewardName].count += 1;
      return acc;
    }, {});
    
    return Object.values(redemptionsByReward || {});
  };

  const fetchUsersByTier = async () => {
    const { data, error } = await supabase
      .from('customer_profiles')
      .select('tier');
      
    if (error) throw error;
    
    // Group by tier
    const usersByTier = data?.reduce((acc: any, profile) => {
      const tier = profile.tier || 'standard';
      
      if (!acc[tier]) {
        acc[tier] = { name: tier, value: 0 };
      }
      
      acc[tier].value += 1;
      return acc;
    }, {});
    
    return Object.values(usersByTier || {});
  };

  const fetchActiveUsers = async () => {
    // Get date range filter
    const { startDate } = getDateRange();
    
    const { data, error } = await supabase
      .from('point_transactions')
      .select('created_at, customer_id')
      .gte('created_at', startDate.toISOString());
      
    if (error) throw error;
    
    // Group by day and count unique users
    const activeUsersByDay = data?.reduce((acc: any, transaction) => {
      const date = new Date(transaction.created_at).toISOString().split('T')[0];
      
      if (!acc[date]) {
        acc[date] = { date, activeUsers: new Set() };
      }
      
      acc[date].activeUsers.add(transaction.customer_id);
      return acc;
    }, {});
    
    // Convert Sets to counts
    return Object.values(activeUsersByDay || {}).map((day: any) => ({
      date: day.date,
      count: day.activeUsers.size
    }));
  };

  const fetchEventParticipation = async () => {
    // Get date range filter
    const { startDate } = getDateRange();
    
    const { data, error } = await supabase
      .from('event_participants')
      .select('event_id, created_at, events(name)')
      .gte('created_at', startDate.toISOString());
      
    if (error) throw error;
    
    // Group by event
    const participationByEvent = data?.reduce((acc: any, participant) => {
      const eventName = (participant.events as any)?.name || 'Unknown Event';
      
      if (!acc[eventName]) {
        acc[eventName] = { name: eventName, participants: 0 };
      }
      
      acc[eventName].participants += 1;
      return acc;
    }, {});
    
    return Object.values(participationByEvent || {});
  };

  const fetchPointsDistribution = async () => {
    const { data, error } = await supabase
      .from('point_transactions')
      .select('source, amount')
      .eq('transaction_type', 'earned');
      
    if (error) throw error;
    
    // Group by source
    const pointsBySource = data?.reduce((acc: any, transaction) => {
      const source = transaction.source || 'Unknown';
      
      if (!acc[source]) {
        acc[source] = { name: source, points: 0 };
      }
      
      acc[source].points += transaction.amount;
      return acc;
    }, {});
    
    return Object.values(pointsBySource || {});
  };

  const fetchTierConversionRates = async () => {
    // This would typically need more complex calculations
    // For now, return some placeholder data
    const tiers = ['standard', 'silver', 'gold', 'platinum', 'admin'];
    const rates = [100, 40, 20, 5, 2]; // Example percentages
    
    return tiers.map((tier, index) => ({
      name: tier,
      rate: rates[index]
    }));
  };

  const getDateRange = () => {
    const now = new Date();
    let startDate = new Date();
    
    switch(dateRange) {
      case 'week':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(now.getMonth() - 1);
        break;
      case 'quarter':
        startDate.setMonth(now.getMonth() - 3);
        break;
      case 'year':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        startDate.setDate(now.getDate() - 7);
    }
    
    return { startDate, endDate: now };
  };

  const exportAnalyticsData = () => {
    try {
      // Get current data based on active tab and metric
      let dataToExport: any[] = [];
      switch (activeTab) {
        case 'users':
          dataToExport = metric === 'signups' ? analyticsData.userSignups : analyticsData.activeUsers;
          break;
        case 'points':
          dataToExport = metric === 'activity' ? analyticsData.pointsActivity : analyticsData.pointsDistribution;
          break;
        case 'rewards':
          dataToExport = analyticsData.rewardRedemptions;
          break;
        case 'events':
          dataToExport = analyticsData.eventParticipation;
          break;
        default:
          dataToExport = analyticsData.userSignups;
      }

      // Convert to CSV
      const headers = Object.keys(dataToExport[0] || {}).join(',');
      const rows = dataToExport.map(item => Object.values(item).join(',')).join('\n');
      const csv = `${headers}\n${rows}`;
      
      // Create download
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.setAttribute('href', url);
      a.setAttribute('download', `analytics-${activeTab}-${metric}-${new Date().toISOString().split('T')[0]}.csv`);
      a.click();
      window.URL.revokeObjectURL(url);

      toast({
        title: t('admin.exportComplete'),
        description: t('admin.fileDownloaded'),
      });
    } catch (error) {
      console.error('Error exporting analytics data:', error);
      toast({
        title: t('admin.exportError'),
        description: t('admin.errorExportingData'),
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">{t('admin.analytics')}</h2>
        <div className="flex items-center space-x-2">
          <CalendarRange className="h-4 w-4 text-muted-foreground" />
          <Select 
            value={dateRange}
            onValueChange={setDateRange}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={t('admin.last7Days')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">{t('admin.last7Days')}</SelectItem>
              <SelectItem value="month">{t('admin.lastMonth')}</SelectItem>
              <SelectItem value="quarter">{t('admin.last3Months')}</SelectItem>
              <SelectItem value="year">{t('admin.lastYear')}</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={exportAnalyticsData}>
            <Download className="mr-2 h-4 w-4" />
            {t('admin.exportAnalytics')}
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="users">{t('admin.users')}</TabsTrigger>
          <TabsTrigger value="points">{t('admin.points')}</TabsTrigger>
          <TabsTrigger value="rewards">{t('admin.rewards')}</TabsTrigger>
          <TabsTrigger value="events">{t('admin.events')}</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <div>
              <Select value={metric} onValueChange={setMetric}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder={t('admin.metric')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="signups">{t('admin.userSignups')}</SelectItem>
                  <SelectItem value="active">{t('admin.activeUsers')}</SelectItem>
                  <SelectItem value="tiers">{t('admin.usersByTier')}</SelectItem>
                  <SelectItem value="conversion">{t('admin.tierConversion')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {metric === 'signups' && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('admin.userSignups')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={analyticsData.userSignups}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="count" stroke="#E4002B" activeDot={{ r: 8 }} name={t('admin.signups')} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {metric === 'active' && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('admin.activeUsers')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={analyticsData.activeUsers}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="count" stroke="#8884d8" activeDot={{ r: 8 }} name={t('admin.activeUsers')} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {metric === 'tiers' && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('admin.usersByTier')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={analyticsData.usersByTier}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        >
                          {analyticsData.usersByTier.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {metric === 'conversion' && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('admin.tierConversionRates')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={analyticsData.tierConversionRate}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="rate" fill="#82ca9d" name={t('admin.conversionRate')} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
            
            <Card>
              <CardHeader>
                <CardTitle>{t('admin.analyticsInsights')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <div className="text-sm">{t('admin.totalUsers')}</div>
                    <div className="font-semibold">
                      {analyticsData.usersByTier.reduce((sum, item) => sum + item.value, 0)}
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="text-sm">{t('admin.newUsersThisPeriod')}</div>
                    <div className="font-semibold">
                      {analyticsData.userSignups.reduce((sum, item) => sum + item.count, 0)}
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="text-sm">{t('admin.activeUsersThisPeriod')}</div>
                    <div className="font-semibold">
                      {analyticsData.activeUsers.length > 0 
                        ? analyticsData.activeUsers[analyticsData.activeUsers.length - 1].count 
                        : 0}
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="text-sm">{t('admin.mostPopularTier')}</div>
                    <div className="font-semibold">
                      {analyticsData.usersByTier.length > 0 
                        ? analyticsData.usersByTier.reduce((prev, current) => 
                            (prev.value > current.value) ? prev : current
                          ).name 
                        : '-'}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="points" className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <div>
              <Select value={metric} onValueChange={setMetric}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder={t('admin.metric')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="activity">{t('admin.pointsActivity')}</SelectItem>
                  <SelectItem value="distribution">{t('admin.pointsDistribution')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {metric === 'activity' && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('admin.pointsActivity')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={analyticsData.pointsActivity}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="earned" stroke="#28a745" name={t('admin.pointsEarned')} />
                        <Line type="monotone" dataKey="redeemed" stroke="#E4002B" name={t('admin.pointsRedeemed')} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {metric === 'distribution' && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('admin.pointsDistribution')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={analyticsData.pointsDistribution}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="points"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        >
                          {analyticsData.pointsDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
            
            <Card>
              <CardHeader>
                <CardTitle>{t('admin.pointsInsights')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <div className="text-sm">{t('admin.totalPointsEarned')}</div>
                    <div className="font-semibold">
                      {analyticsData.pointsActivity.reduce((sum, item) => sum + item.earned, 0)}
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="text-sm">{t('admin.totalPointsRedeemed')}</div>
                    <div className="font-semibold">
                      {analyticsData.pointsActivity.reduce((sum, item) => sum + item.redeemed, 0)}
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="text-sm">{t('admin.netPointsChange')}</div>
                    <div className="font-semibold">
                      {analyticsData.pointsActivity.reduce((sum, item) => sum + item.earned - item.redeemed, 0)}
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="text-sm">{t('admin.topPointsSource')}</div>
                    <div className="font-semibold">
                      {analyticsData.pointsDistribution.length > 0 
                        ? analyticsData.pointsDistribution.reduce((prev, current) => 
                            (prev.points > current.points) ? prev : current
                          ).name 
                        : '-'}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="rewards" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('admin.rewardRedemptions')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={analyticsData.rewardRedemptions}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="count" fill="#8884d8" name={t('admin.redemptions')} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>{t('admin.rewardInsights')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <div className="text-sm">{t('admin.totalRedemptions')}</div>
                    <div className="font-semibold">
                      {analyticsData.rewardRedemptions.reduce((sum, item) => sum + item.count, 0)}
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="text-sm">{t('admin.mostPopularReward')}</div>
                    <div className="font-semibold">
                      {analyticsData.rewardRedemptions.length > 0 
                        ? analyticsData.rewardRedemptions.reduce((prev, current) => 
                            (prev.count > current.count) ? prev : current
                          ).name 
                        : '-'}
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="text-sm">{t('admin.redemptionRate')}</div>
                    <div className="font-semibold">
                      {analyticsData.rewardRedemptions.length > 0 && analyticsData.userSignups.length > 0
                        ? `${((analyticsData.rewardRedemptions.reduce((sum, item) => sum + item.count, 0) / 
                               analyticsData.usersByTier.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(1)}%`
                        : '-'}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="events" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('admin.eventParticipation')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={analyticsData.eventParticipation}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="participants" fill="#E4002B" name={t('admin.participants')} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>{t('admin.eventInsights')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <div className="text-sm">{t('admin.totalEvents')}</div>
                    <div className="font-semibold">
                      {analyticsData.eventParticipation.length}
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="text-sm">{t('admin.totalParticipants')}</div>
                    <div className="font-semibold">
                      {analyticsData.eventParticipation.reduce((sum, item) => sum + item.participants, 0)}
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="text-sm">{t('admin.averageParticipantsPerEvent')}</div>
                    <div className="font-semibold">
                      {analyticsData.eventParticipation.length > 0
                        ? (analyticsData.eventParticipation.reduce((sum, item) => sum + item.participants, 0) / 
                           analyticsData.eventParticipation.length).toFixed(1)
                        : '-'}
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="text-sm">{t('admin.mostPopularEvent')}</div>
                    <div className="font-semibold">
                      {analyticsData.eventParticipation.length > 0 
                        ? analyticsData.eventParticipation.reduce((prev, current) => 
                            (prev.participants > current.participants) ? prev : current
                          ).name 
                        : '-'}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminAnalytics;
